import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { useGoogleLogin } from '@react-oauth/google';
import { getApiUrl } from '../config';

interface LoginModalProps {
  isOpen: boolean;
  onClose: () => void;
  onLoginSuccess: (token: string, user: any) => void;
}

const LoginModal: React.FC<LoginModalProps> = ({ isOpen, onClose, onLoginSuccess }) => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [googleLoading, setGoogleLoading] = useState(false);
  const [error, setError] = useState('');
  const [statusMessage, setStatusMessage] = useState('');

  useEffect(() => {
    if (!isOpen) {
      setEmail('');
      setPassword('');
      setError('');
      setStatusMessage('');
      setLoading(false);
      setGoogleLoading(false);
    }
  }, [isOpen]);

  const getRequestErrorMessage = (err: any, fallback: string) => {
    if (err?.response?.data?.error) {
      return err.response.data.error;
    }

    if (err?.code === 'ECONNABORTED') {
      return 'La conexión tardó demasiado. Inténtalo de nuevo.';
    }

    if (err?.message === 'Network Error') {
      return 'No se pudo conectar con el servidor. Revisa tu conexión e inténtalo de nuevo.';
    }

    return fallback;
  };

  const handleLogin = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setStatusMessage('Iniciando sesión...');
    setLoading(true);

    const pendingFeedbackTimer = window.setTimeout(() => {
      setStatusMessage('La verificación está tardando más de lo normal...');
    }, 5000);

    try {
      const response = await axios.post(
        getApiUrl('/api/auth/login'),
        { email, password },
        { timeout: 12000 }
      );
      const { user } = response.data;
      localStorage.setItem('user', JSON.stringify(user));
      setStatusMessage('Sesión iniciada. Redirigiendo...');
      onLoginSuccess('', user);
      onClose();
    } catch (err: any) {
      setError(getRequestErrorMessage(err, 'Error al iniciar sesión'));
      setStatusMessage('');
    } finally {
      window.clearTimeout(pendingFeedbackTimer);
      setLoading(false);
    }
  };

  const handleGoogleLogin = useGoogleLogin({
    onSuccess: async (tokenResponse) => {
      setGoogleLoading(true);
      setError('');
      setStatusMessage('Conectando con Google...');
      try {
        const response = await axios.post(
          getApiUrl('/api/auth/google'),
          {
            accessToken: tokenResponse.access_token,
            mode: 'login',
          },
          { timeout: 12000 }
        );
        const { user } = response.data;
        localStorage.setItem('user', JSON.stringify(user));
        setStatusMessage('Sesión iniciada. Redirigiendo...');
        onLoginSuccess('', user);
        onClose();
      } catch (err: any) {
        setError(getRequestErrorMessage(err, 'Error al iniciar sesión con Google'));
        setStatusMessage('');
      } finally {
        setGoogleLoading(false);
      }
    },
    onError: () => {
      setStatusMessage('');
      setError('Error al iniciar sesión con Google');
    },
  });

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black/80 flex items-center justify-center z-50" onClick={onClose}>
      <div className="card-gaming max-w-md w-full mx-4" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between mb-6">
          <h2 className="font-gaming text-gaming-cyan text-lg tracking-wider">INICIAR SESIÓN</h2>
          <button
            onClick={onClose}
            className="text-white/40 hover:text-white transition-colors text-2xl"
          >
            ×
          </button>
        </div>

        <form onSubmit={handleLogin} className="space-y-4">
          {error && (
            <div className="bg-red-500/20 border border-red-500 text-red-400 px-3 py-2 rounded text-sm font-main">
              {error}
            </div>
          )}

          {!error && statusMessage && (
            <div className="bg-gaming-cyan/10 border border-gaming-cyan/40 text-gaming-cyan px-3 py-2 rounded text-sm font-main">
              {statusMessage}
            </div>
          )}

          <div>
            <label className="block font-main text-white/70 text-sm mb-2">Email</label>
            <input
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              className="w-full bg-white/5 border border-white/10 rounded px-3 py-2 text-white font-main focus:outline-none focus:border-gaming-cyan"
              required
            />
          </div>

          <div>
            <label className="block font-main text-white/70 text-sm mb-2">Contraseña</label>
            <input
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              className="w-full bg-white/5 border border-white/10 rounded px-3 py-2 text-white font-main focus:outline-none focus:border-gaming-cyan"
              required
            />
          </div>

          <button
            type="submit"
            disabled={loading}
            className="btn-primary w-full py-3 font-gaming text-sm tracking-wider disabled:opacity-50"
          >
            {loading ? 'Iniciando...' : 'INICIAR SESIÓN'}
          </button>
        </form>

        <div className="my-4 border-t border-white/10"></div>

        <button
          onClick={() => handleGoogleLogin()}
          disabled={googleLoading}
          className="w-full bg-white/5 hover:bg-white/10 border border-white/10 rounded px-3 py-3 text-white font-main text-sm transition-colors flex items-center justify-center gap-2 disabled:opacity-50"
        >
          <img src="https://www.google.com/favicon.ico" alt="Google" className="w-4 h-4" />
          {googleLoading ? 'Conectando...' : 'Continuar con Google'}
        </button>
      </div>
    </div>
  );
};

export default LoginModal;
